/* eslint max-lines: ["error", { max: 261 }] -- this overrides for the entire file */
import { format } from "date-fns";
import isNil from "lodash/isNil";

import { DateFormat } from "@/constants/DateFormat";
import type { AvatarCardProps } from "@/ui/core";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { getPreferredFullName } from "@/utils/user.util";

const bookingAttendeeToBaseUserAvatar = ({
  chosenName,
  externalId,
  firstName,
  lastName,
  userType,
}: BookingAttendee): AvatarCardProps => {
  const userName = getPreferredFullName({
    chosenName,
    firstName,
    lastName,
  });

  return {
    userId: externalId,
    userName,
    userType,
  };
};

const bookingProviderTypeToBaseUserAvatar = ({
  providerExternalId,
  providerName,
  providerType,
}: BookingProviderType): AvatarCardProps => {
  return {
    subtitle: providerType,
    userId: providerExternalId,
    userName: providerName,
    userType: "provider",
  };
};

interface GetRequiredAttendeesArgs {
  attendees?: BookingAttendee[];
  providerType?: BookingProviderType;
}

const getRequiredAttendees = ({
  attendees,
  providerType,
}: GetRequiredAttendeesArgs): AvatarCardProps[] => {
  const baseAttendees: AvatarCardProps[] = [];

  if (providerType) {
    baseAttendees.push(bookingProviderTypeToBaseUserAvatar(providerType));
  }

  attendees
    ?.filter((attendee) => attendee.isRequired)
    .forEach((attendee) => {
      baseAttendees.push(bookingAttendeeToBaseUserAvatar(attendee));
    });

  return baseAttendees;
};

interface GetOptionalAttendeesArgs {
  attendees?: BookingAttendee[];
}

const getOptionalAttendees = ({
  attendees,
}: GetOptionalAttendeesArgs): AvatarCardProps[] => {
  if (isNil(attendees)) {
    return [];
  }

  return attendees
    .filter((attendee) => !attendee.isRequired)
    .map((attendee) => bookingAttendeeToBaseUserAvatar(attendee));
};

interface GetBookingAttendeesArgs {
  attendees?: BookingAttendee[];
  optionalAttendees?: BookingAttendee[];
  providerType?: BookingProviderType;
}
const getBookingAttendees = ({
  attendees,
  optionalAttendees,
  providerType,
}: GetBookingAttendeesArgs): AvatarCardProps[] => {
  const baseAttendees: AvatarCardProps[] = [];

  const requiredAttendees = getRequiredAttendees({
    attendees,
    providerType,
  });

  baseAttendees.push(...requiredAttendees);

  optionalAttendees?.forEach((attendee) => {
    baseAttendees.push(bookingAttendeeToBaseUserAvatar(attendee));
  });

  return baseAttendees;
};

const groupTimeSlotsByDay = (timeSlots?: TimeSlot[]): TimeSlotGroup[] => {
  if (isNil(timeSlots)) {
    return [];
  }

  const normalizedGroupedTimeSlots: NormalizedTimeSlots = timeSlots.reduce(
    // eslint-disable-next-line max-params
    (slots: NormalizedTimeSlots, slot: TimeSlot) => {
      const formattedDate = format(
        new Date(slot.start),
        DateFormat.shortNumberMonthDayYear,
      );

      const currentDaySlots = slots[formattedDate] ?? [];
      const daySlots = [...currentDaySlots, slot];

      return {
        ...slots,
        [formattedDate]: daySlots,
      };
    },
    {},
  );

  const timeSlotGroups: TimeSlotGroup[] = Object.keys(
    normalizedGroupedTimeSlots,
  ).map((key) => {
    return {
      date: key,
      timeSlots: normalizedGroupedTimeSlots[key],
    };
  });

  return timeSlotGroups;
};

// Avoid duplicating "Session" in the title if the appointment title already includes it
const addSessionSuffixIfMissing = (appointmentTitle?: string) => {
  if (!isNotNilNotEmpty(appointmentTitle)) {
    return "Session";
  }

  return appointmentTitle.endsWith("Session")
    ? appointmentTitle
    : `${appointmentTitle} Session`;
};

interface GetSelectedProviderProps {
  providerExternalId?: string;
  providerType?: string;
  selectedProviderData?: SelectedProviderData;
}

interface SelectedProviderData {
  providerExternalId?: string;
  providerType?: string;
}

// Comparing both provider ID and provider type to account for providers with multiple provider types
const isProviderSelected = ({
  providerExternalId,
  providerType,
  selectedProviderData,
}: GetSelectedProviderProps) => {
  return (
    selectedProviderData?.providerExternalId === providerExternalId &&
    selectedProviderData?.providerType === providerType
  );
};

const getUniqueAppointmentTypes = (appointmentTypes: AppointmentType[]) => {
  return Array.from(
    new Map(
      appointmentTypes.map((item) => [item.appointmentTypeGroup, item]),
    ).values(),
  );
};

interface ShouldShowSessionTypeStepArgs {
  selectedProvider?: BookingProviderType;
}

const shouldShowSessionTypeStep = ({
  selectedProvider,
}: ShouldShowSessionTypeStepArgs): boolean => {
  if (isNil(selectedProvider?.appointmentTypes)) {
    return false;
  }

  return (
    getUniqueAppointmentTypes(selectedProvider.appointmentTypes).length > 1
  );
};

export type { SelectedProviderData };
export {
  addSessionSuffixIfMissing,
  bookingAttendeeToBaseUserAvatar,
  bookingProviderTypeToBaseUserAvatar,
  getBookingAttendees,
  getOptionalAttendees,
  getRequiredAttendees,
  getUniqueAppointmentTypes,
  groupTimeSlotsByDay,
  isProviderSelected,
  shouldShowSessionTypeStep,
};
